import { ROUTE_ORDER_MANAGEMENT } from "@/constants/routes.constants";
import { Card, CardContent, CardHeader } from "@/shadcn/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/shadcn/components/ui/chart";
import { DashboardCountOrderData, SimpleOrder } from "@/types";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Cell, Legend, Pie, PieChart } from "recharts";

type DashboardGraphOrdersProps = {
  ordersCounts: DashboardCountOrderData;
};

const DashboardGraphOrders: React.FC<DashboardGraphOrdersProps> = ({
  ordersCounts,
}) => {
  const { t } = useTranslation();
  const nav = useNavigate();

  // Structure data to match the pie chart's expected format, ensuring correct mapping of order statuses.
  const pieData: {
    navKey: SimpleOrder["status"];
    name: string;
    value: number;
  }[] = [
    {
      navKey: "in_progress",
      name: t("In Progress"),
      value: ordersCounts.inProgress,
    },
    {
      navKey: "completed",
      name: t("Completed"),
      value: ordersCounts.completed,
    },
  ];

  function navigateWithFilter(status: SimpleOrder["status"]) {
    nav(ROUTE_ORDER_MANAGEMENT, {
      state: {
        filter: {
          status: status,
        },
      },
    });
  }

  return (
    <Card className="rounded-lg">
      <CardHeader className="text-center">
        <div className="text-xl font-bold">{t("Orders")}</div>
        <div>Total: {ordersCounts.total}</div>
      </CardHeader>
      <CardContent>
        <ChartContainer
          config={{
            value: { label: t("Orders") },
          }}
          className="min-h-[200px] w-full"
        >
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={"80%"}
              labelLine={false}
              className="cursor-pointer"
            >
              {pieData.map((entry, idx) => (
                <Cell
                  key={`cell-${idx}`}
                  fill={
                    entry.navKey === "in_progress"
                      ? "var(--color-order-in-progress)"
                      : "var(--color-order-completed)"
                  }
                  onClick={() => navigateWithFilter(entry.navKey)}
                />
              ))}
            </Pie>
            <Legend
              align="center"
              verticalAlign="bottom"
              iconType="circle"
              content={({ payload }) => (
                <ul
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 24,
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                  }}
                >
                  {payload &&
                    payload.map((entry, idx) => (
                      <li
                        key={entry.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        onClick={() => navigateWithFilter(pieData[idx].navKey)}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            backgroundColor: entry.color,
                            marginRight: "5px",
                          }}
                        />
                        <span
                          style={{
                            color: entry.color,
                          }}
                        >
                          {entry.value}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
          </PieChart>
        </ChartContainer>
        <div className="mt-4 flex justify-center">
          <button
            className="group border-primary text-primary relative inline-flex cursor-pointer items-center overflow-hidden rounded-md border-2 px-10 py-2 text-sm font-medium hover:bg-transparent hover:text-black"
            onClick={() => nav(ROUTE_ORDER_MANAGEMENT)}
          >
            <span className="ease bg-primary absolute top-1/2 left-0 block h-0 w-full opacity-100 transition-all duration-400 group-hover:top-0 group-hover:h-full"></span>
            <span className="ease absolute -right-2 flex h-10 w-10 translate-x-full transform items-center justify-start duration-300 group-hover:translate-x-0">
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                ></path>
              </svg>
            </span>
            <span className="relative">{t("View All Orders")}</span>
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardGraphOrders;
